import React from 'react';
import './MaintenanceDetailModal.css';
import { LogType } from '../interfaces/LogType';

interface MaintenanceDetailModalProps {
  log: LogType;
  logNumber: number;
  onClose: () => void;
}

const MaintenanceDetailModal: React.FC<MaintenanceDetailModalProps> = ({
  log,
  logNumber,
  onClose,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const calculateDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationSeconds = Math.floor(
      (end.getTime() - start.getTime()) / 1000
    );
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  const calculateMaxSpeedKmH = (log: LogType) => {
    const vx = log.xSpeedMax;
    const vy = log.ySpeedMax;
    const vz = log.zSpeedMax;
    const maxSpeedMs = Math.sqrt(vx * vx + vy * vy + vz * vz);
    const maxSpeedKmH = maxSpeedMs * 3.6;
    return Math.round(maxSpeedKmH);
  };

  const flightTimeInMinutes =
  (new Date(log.endTime).getTime() -
    new Date(log.startTime).getTime()) /
  60000;
const batteryConsumption =
  log.battery.chargeLevelStart - log.battery.chargeLevelEnd;

const consumptionPerMinute =
  flightTimeInMinutes > 0
    ? batteryConsumption / flightTimeInMinutes
    : 0;
const roundedConsumptionPerMinute = Math.round(consumptionPerMinute * 10) / 10;

  const maxSpeed = calculateMaxSpeedKmH(log);
  const flightDuration = calculateDuration(log.startTime, log.endTime);
  const roundedHeightMax = Math.round(log.heightMax);
  const roundedMinTemp = Math.round(log.battery.minTemperature);
  const roundedMaxTemp = Math.round(log.battery.maxTemperature);

  const errorFields = [
    { key: 'waveError', label: 'Wellenfehler' },
    { key: 'isMotorBlocked', label: 'Motor blockiert' },
    {
      key: 'isAcceletorOverRange',
      label: 'Beschleunigungssensor überschritten',
    },
    { key: 'isBarometerDeadInAir', label: 'Barometer ausgefallen' },
    { key: 'isCompassError', label: 'Kompassfehler' },
    { key: 'isNotEnoughForce', label: 'Nicht genug Schub' },
    { key: 'isPropellerCatapult', label: 'Propellerkatapult' },
    { key: 'isVibrating', label: 'Vibrationen' },
    { key: 'isStuck', label: 'Blockierung' },
  ];

  return (
    <div className="maintenance-modal-overlay" onClick={onClose}>
      <div
        className="maintenance-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="maintenance-button-container">
          <button
            className="maintenance-modal-button maintenance-material-icons maintenance-close-button"
            onClick={onClose}
          >
            close
          </button>
        </div>

        <div className="maintenance-modal-header">
          <h2>Wartungsdetails</h2>
        </div>

        <div className="maintenance-modal-body-container">
          <div className="maintenance-modal-body">
            <div className="maintenance-modal-left">
              <div className="maintenance-modal-details">
                <div className="maintenance-modal-group">
                  <div>
                    <strong>Flug Nr.:</strong> {logNumber}
                  </div>
                </div>

                <div className="maintenance-modal-group">
                  <div>
                    <strong>Datum:</strong> {formatDate(log.startTime)}
                  </div>
                </div>

                <div className="maintenance-modal-group">
                  <div>
                    <strong>Ort:</strong> {log.location || 'Unbekannt'}
                  </div>
                </div>

                <div className="maintenance-modal-group">
                  <div>
                    <strong>Flugdauer:</strong> {flightDuration}
                  </div>
                </div>

                <div className="maintenance-modal-group">
                  <div>
                    <strong>Verbrauch:</strong> {roundedConsumptionPerMinute}% pro min
                  </div>
                </div>

                <div className="maintenance-modal-group">
                  <div>
                    <strong>max. Höhe:</strong> {roundedHeightMax} m
                  </div>
                </div>

                <div className="maintenance-modal-group">
                  <div>
                    <strong>max. km/h:</strong> {maxSpeed} km/h
                  </div>
                </div>

                <div className="maintenance-modal-group">
                  <div>
                    <strong>min. Temp.:</strong> {roundedMinTemp} °C
                  </div>
                </div>

                <div className="maintenance-modal-group">
                  <div>
                    <strong>max. Temp.:</strong> {roundedMaxTemp} °C
                  </div>
                </div>
              </div>
            </div>

            <div className="maintenance-modal-right">
              <div className="maintenance-error-overview no-scroll">
                <h3>Fehlerübersicht</h3>
                <div className="maintenance-error-list">
                  {errorFields.map((field) => {
                    const value = (log.errors as any)[field.key];
                    return (
                      <div className="maintenance-error-item" key={field.key}>
                        <span className="maintenance-error-label">
                          {field.label}:
                        </span>
                        <span
                          className="maintenance-material-icons maintenance-error-icon"
                          style={{ color: value ? '#c20000' : 'green' }}
                        >
                          {value ? 'check_circle' : 'cancel'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDetailModal;
